import "../styling/PaymentMethodsStyles.css";
import { useState, useEffect, useMemo, useRef } from "react";
import { BasisTheoryProvider, CardElement } from "@basis-theory/react-elements";
import { toast } from "react-toastify";
import { useStyles } from "../../../context/StyleContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { useCheckout } from "../../../context/CheckoutContext";
import { fetchCheckoutDetails, fetchGatewayConnection, processPayment } from "../../../api/api";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import {
    handleCitPaymentApiResponse,
    applyCitDeclineOrErrorUi,
    CIT_OUTCOME,
} from "../../../utils/paymentOutcome";
import { mapStylesToBasisTheoryTheme, basisTheoryEnvironment } from "../../../utils/basisTheoryStyleMapper";
import { useBasisTheoryCard, isVaultNotEnabledError } from "../../../hooks/useBasisTheoryCard";
import useWebSocket from "../../../hooks/useWebSocket";
import PaymentProcessingModal from "../../modal/PaymentProcessingModal";
import TransactionStatusModal from "../../modal/TransactionStatusModal";
import CheckoutComCardForm from "../checkout/CheckoutComCardForm";

export const VAULT_PROVIDER_BASIS_THEORY = "basis_theory";

const findCardRouting = (routingEntries) =>
    routingEntries?.find(
        (entry) => entry.paymentMethod === "card" && entry.platform === "checkoutcom"
    );

/**
 * Card form for checkouts where Altruon vaults the card (Basis Theory), charged through
 * Checkout.com. Falls back to the Checkout.com Frames form whenever Basis Theory cannot be used:
 * Elements fail to load, or the backend reports vaulting is off for this checkout. The backend
 * keeps accepting Frames tokens for the same connection, so the fallback always works.
 */
const BasisTheoryCardForm = (props) => {
    const { routingEntries } = props;
    const { styles } = useStyles();
    const [useGatewayForm, setUseGatewayForm] = useState(false);

    const vaulting = findCardRouting(routingEntries)?.vaulting;
    const publicKey = vaulting?.provider === VAULT_PROVIDER_BASIS_THEORY ? vaulting?.publicKey : null;

    const options = useMemo(
        () => ({
            environment: basisTheoryEnvironment(vaulting?.apiBaseUrl),
            theme: mapStylesToBasisTheoryTheme(styles),
        }),
        [vaulting?.apiBaseUrl, styles]
    );

    if (useGatewayForm || !publicKey) {
        return <CheckoutComCardForm {...props} />;
    }

    return (
        <BasisTheoryProvider
            apiKey={publicKey}
            options={options}
            errorFallback={<GatewayFormOffer onUseGatewayForm={() => setUseGatewayForm(true)} />}
        >
            <BasisTheoryCardFormInner {...props} onUseGatewayForm={() => setUseGatewayForm(true)} />
        </BasisTheoryProvider>
    );
};

const GatewayFormOffer = ({ onUseGatewayForm }) => {
    const { t } = useTranslation();
    return (
        <div className="checkout-card-container">
            <div className="error-message">
                {t("paymentForms.card.secureFormUnavailable", {
                    defaultValue: "The card form could not be loaded.",
                })}
            </div>
            <button type="button" className="btn btn-link p-0" onClick={onUseGatewayForm}>
                {t("paymentForms.card.useAlternativeForm", {
                    defaultValue: "Use the alternative card form",
                })}
            </button>
        </div>
    );
};

const BasisTheoryCardFormInner = ({
    formData,
    itemDetails,
    billingPlatformId,
    routingEntries,
    planId,
    prefetchedGatewayConnection,
    sessionId,
    checkoutPageConfDto,
    isEmbeddedCheckout = false,
    onParentCallback,
    onUseGatewayForm,
}) => {
    const { t } = useTranslation();
    const {
        triggerPaymentMethodEvent,
        resetTrigger,
        clientIp,
        clientCountry,
        paymentMethodName,
        platformName,
        isHostedPageMode,
        citPaymentErrorMessage,
    } = useCheckout();

    const {
        sdkError,
        cardRef,
        ready,
        complete,
        fieldError,
        onReady,
        onChange,
        createTokenIntent,
    } = useBasisTheoryCard({ sessionId });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [transactionStatus, setTransactionStatus] = useState("");
    const [checkoutConfDto, setCheckoutConfDto] = useState(checkoutPageConfDto || null);
    const [showProcessingModal, setProcessingModal] = useState(false);
    const [transactionId, setTransactionId] = useState(null);
    const [hostedPageRedirect, setHostedPageRedirect] = useState(null);
    const isProcessingRef = useRef(false);

    const cardRouting = useMemo(() => findCardRouting(routingEntries), [routingEntries]);
    const cardholderName =
        formData?.firstName && formData?.lastName
            ? `${formData.firstName} ${formData.lastName}`.trim()
            : "";

    useEffect(() => {
        if (checkoutPageConfDto) {
            setCheckoutConfDto(checkoutPageConfDto);
            return;
        }
        if (billingPlatformId && planId) {
            fetchCheckoutDetails(billingPlatformId, planId)
                .then((data) => data?.checkoutPageConfDto && setCheckoutConfDto(data.checkoutPageConfDto))
                .catch(() => toast.error(t("messages.error.somethingWrong") || "Something went wrong."));
        }
    }, [billingPlatformId, planId, checkoutPageConfDto]);

    useEffect(() => {
        if (!citPaymentErrorMessage) return;
        setError(citPaymentErrorMessage);
        setLoading(false);
        isProcessingRef.current = false;
        setProcessingModal(false);
    }, [citPaymentErrorMessage]);

    const buildPayload = (intent, gatewayConnection, latestSession) => ({
        paymentData: {
            gatewayConnectionId: gatewayConnection.id,
            gatewayName: gatewayConnection.platform,
            paymentMethod: "card",
            transactionId: "",
            additionalData: {
                payment_method_flow: "DIRECT",
                vault: VAULT_PROVIDER_BASIS_THEORY,
                tokenIntentId: intent.tokenIntentId,
                cardholderName,
                card_brand: intent.brand,
                save: true,
                capture: true,
                verify: false,
            },
        },
        customerData: {
            firstName: formData?.firstName || latestSession?.customerData?.firstName,
            lastName: formData?.lastName || latestSession?.customerData?.lastName,
            email: formData?.email || latestSession?.customerData?.email,
            phone: formData?.phone || latestSession?.customerData?.phone || "9908734801",
            ip: clientIp || "127.0.0.1",
            billingAddress: {
                country: formData?.billingAddress?.country || latestSession?.customerData?.billingAddress?.country || clientCountry || "US",
                zipCode: formData?.billingAddress?.zipCode || latestSession?.customerData?.billingAddress?.zipCode || "90210",
                street: formData?.billingAddress?.street || latestSession?.customerData?.billingAddress?.street || "",
                city: formData?.billingAddress?.city || latestSession?.customerData?.billingAddress?.city || "",
                state: formData?.billingAddress?.state || latestSession?.customerData?.billingAddress?.state || "",
                number: "1234",
            },
        },
        billingData: {
            planId: planId,
            billingPlatformId,
            billingPlatformName: itemDetails?.billingPlatformName || latestSession?.billingData?.billingPlatformName || "",
            frequency: latestSession?.billingData?.frequency || "",
        },
    });

    const handleProcessPayment = async (payload, intent) => {
        if (isEmbeddedCheckout) {
            onParentCallback?.({
                type: "finalizeFormData",
                data: {
                    paymentMethod: "card",
                    gatewayName: "checkoutcom",
                    vault: VAULT_PROVIDER_BASIS_THEORY,
                    tokenIntentId: intent.tokenIntentId,
                    cardBrand: intent.brand,
                    saveCard: true,
                },
            });
            await new Promise((r) => setTimeout(r, 800));
        } else {
            await updateSession(sessionId, payload);
        }
        return processPayment(billingPlatformId, sessionId, payload);
    };

    const citUi = () => ({
        isHostedPageMode,
        isEmbeddedCheckout,
        t,
        setError,
        setTransactionStatus,
        setShowStatusModal,
        onParentCallback,
        toast,
    });

    const finishProcessing = () => {
        setLoading(false);
        isProcessingRef.current = false;
        if (!isEmbeddedCheckout) {
            setProcessingModal(false);
        }
    };

    const handlePayNow = async () => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;
        setError("");

        if (!cardRouting) {
            setError(t("paymentForms.card.unavailable"));
            isProcessingRef.current = false;
            return;
        }
        if (!ready || !(cardRef.current?.complete ?? complete)) {
            setError(fieldError || t("paymentForms.card.notReady") || "Please complete your card details");
            isProcessingRef.current = false;
            return;
        }

        if (!isEmbeddedCheckout) {
            setProcessingModal(true);
        }
        setLoading(true);

        let intent;
        try {
            intent = await createTokenIntent();
        } catch (e) {
            finishProcessing();
            if (isVaultNotEnabledError(e)) {
                // Vaulting was switched off after this page loaded; nothing was charged.
                onUseGatewayForm?.();
                return;
            }
            setError(formatPaymentError(e?.response?.data?.message || e?.message, t));
            return;
        }

        try {
            const gatewayConnection =
                prefetchedGatewayConnection || (await fetchGatewayConnection(cardRouting, t));
            const latestSession = await fetchSession(sessionId);
            const payload = buildPayload(intent, gatewayConnection, latestSession);
            const res = await handleProcessPayment(payload, intent);

            const { handled } = handleCitPaymentApiResponse(res, citUi());
            if (handled) return;

            if (res.status === 200) {
                if (res?.data?.hosted_redirect_page) {
                    setHostedPageRedirect(res.data.hosted_redirect_page);
                }
                const redirectUrl = res.data.redirect_url && `${res.data.redirect_url}?trxId=${res.data.transactionId}`;
                const requiresRedirect = Boolean(res?.data?.on_redirect_required && redirectUrl);

                setTransactionId(res?.data?.newTransactionId || res?.data?.transactionId);
                if (isEmbeddedCheckout) {
                    if (redirectUrl) {
                        onParentCallback?.({ type: "actionRequired", action: "REDIRECT", url: redirectUrl });
                    } else {
                        onParentCallback?.({ type: "success", transactionId: res.data.transactionId });
                    }
                } else {
                    if (requiresRedirect) {
                        safeRedirect(redirectUrl, { target: "self" });
                        return;
                    }
                    setTransactionStatus("success");
                    setShowStatusModal(true);
                }
            } else {
                applyCitDeclineOrErrorUi(
                    { outcome: CIT_OUTCOME.ERROR, message: t("messages.error.somethingWrong") },
                    citUi()
                );
            }
        } catch (e) {
            if (e?.response && handleCitPaymentApiResponse(e.response, citUi()).handled) return;
            applyCitDeclineOrErrorUi(
                { outcome: CIT_OUTCOME.ERROR, message: formatPaymentError(e?.message, t) },
                citUi()
            );
        } finally {
            finishProcessing();
        }
    };

    useEffect(() => {
        if (
            triggerPaymentMethodEvent &&
            (isEmbeddedCheckout || (platformName === "checkoutcom" && paymentMethodName === "card"))
        ) {
            handlePayNow();
            resetTrigger();
        }
    }, [triggerPaymentMethodEvent, paymentMethodName, platformName, resetTrigger, handlePayNow, isEmbeddedCheckout]);

    useWebSocket(
        isEmbeddedCheckout && transactionId,
        (event) => {
            const data = JSON.parse(event.data);
            if (data?.status === "success") {
                onParentCallback?.({ type: "success", transactionId });
            }
            if (data?.status === "failed") {
                onParentCallback?.({ type: "failure" });
            }
        },
        undefined,
        undefined,
        (err) => console.error("CARD WS error", err)
    );

    if (sdkError) {
        return <GatewayFormOffer onUseGatewayForm={onUseGatewayForm} />;
    }

    return (
        <div className="checkout-card-container">
            <div className="form-section">
                <div className="card-mount-area" style={{ position: "relative" }}>
                    {!ready && (
                        <div className="sdk-loading-text">{t("paymentForms.card.loadingSDK")}</div>
                    )}
                    <CardElement
                        ref={cardRef}
                        className={`bt-card-fields ${!ready ? "loading" : ""}`}
                        layout="auto"
                        stackAt={1}
                        iconPosition="right"
                        placeholder={{
                            cardNumber: "1234 5678 9012 3456",
                            expiryDate: "MM/YY",
                            cvc: "CVV",
                        }}
                        ariaLabel={t("paymentForms.card.label", { defaultValue: "Card details" })}
                        onReady={onReady}
                        onChange={onChange}
                        onError={() => setError(t("messages.error.somethingWrong"))}
                    />
                </div>
                {fieldError && <div className="error-message">{fieldError}</div>}
            </div>

            {error && !fieldError && <div className="error-message">{error}</div>}

            {loading && (
                <div className="loader-overlay">
                    <div className="spinner-large" />
                    <div className="loading-text">{t("paymentForms.card.processing")}</div>
                </div>
            )}

            <PaymentProcessingModal show={showProcessingModal} setShow={setProcessingModal} />

            <TransactionStatusModal
                show={showStatusModal}
                setShow={setShowStatusModal}
                status={transactionStatus}
                transactionId={transactionId}
                hostedPageRedirect={hostedPageRedirect}
                fallbackRedirectUrl={checkoutConfDto?.checkoutRedirectUrl}
            />
        </div>
    );
};

export default BasisTheoryCardForm;
